/**
 * ---------------------------------------------------------------------------
 * Infinite / load-more pagination  (custom addition, not part of Impact)
 * ---------------------------------------------------------------------------
 * Works together with snippets/pagination.liquid, which renders the markup
 * with [data-infinite-pagination] on collection and search pages.
 *
 * Behaviour:
 *   - auto-loads the next page when the user approaches the bottom
 *   - after N automatic loads it shows a "Load more" button instead
 *     (N comes from data-auto-pages; 0 = always auto-load)
 *   - keeps ?page=N in the address bar in sync with what is on screen,
 *     so "back" from a product page returns to roughly the same spot
 *   - everything is re-read from the DOM on each run, so it keeps working
 *     after the theme swaps the section on filter / sort changes
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__infinitePaginationReady) return;
  window.__infinitePaginationReady = true;

  var CONTAINER_SELECTOR = '[data-infinite-pagination]';
  var BOTTOM_THRESHOLD = 800; // px before the pagination block enters the viewport
  var MAX_ERRORS = 3;

  var anchorObserver = null;

  /* ----------------------------------------------------------------- utils */

  function container() {
    return document.querySelector(CONTAINER_SELECTOR);
  }

  function productList(el) {
    var scope = el.closest('.collection') || document;
    return scope.querySelector('product-list') || scope.querySelector('.product-list');
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function buildUrl(page, withSectionId, sectionId) {
    var url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    if (withSectionId && sectionId) {
      url.searchParams.set('section_id', sectionId);
    } else {
      url.searchParams.delete('section_id');
    }
    return url.toString();
  }

  function syncAddressBar(page) {
    try {
      var url = new URL(window.location.href);
      var current = url.searchParams.get('page') || '1';
      if (current === String(page)) return;
      if (String(page) === '1') {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* never break the page over a URL update */
    }
  }

  /* ----------------------------------------------------------------- state */

  function getState(el) {
    if (!el.__infinite) {
      var state = {
        mode: el.getAttribute('data-mode') || 'infinite',
        autoPages: toInt(el.getAttribute('data-auto-pages'), 0),
        autoUsed: 0,
        page: toInt(el.getAttribute('data-current-page'), 1),
        totalPages: toInt(el.getAttribute('data-total-pages'), 1),
        pageSize: toInt(el.getAttribute('data-page-size'), 24),
        totalItems: toInt(el.getAttribute('data-total-items'), 0),
        sectionId: el.getAttribute('data-section-id') || '',
        loading: false,
        errors: 0,
        giveUp: false,
        done: false
      };
      state.done = state.page >= state.totalPages;
      el.__infinite = state;

      // Tag the products that are already on the page, so the address bar can
      // follow the scroll position.
      var list = productList(el);
      if (list && list.firstElementChild) {
        list.firstElementChild.setAttribute('data-page-anchor', String(state.page));
        observeAnchor(list.firstElementChild);
      }

      paint(el, state);
    }
    return el.__infinite;
  }

  /* -------------------------------------------------------------- painting */

  function paint(el, state) {
    var button = el.querySelector('[data-infinite-load-more]');
    var spinner = el.querySelector('[data-infinite-spinner]');
    var shownNode = el.querySelector('[data-infinite-shown]');
    var bar = el.querySelector('[data-infinite-bar]');

    var shown = Math.min(state.page * state.pageSize, state.totalItems || state.page * state.pageSize);

    if (shownNode) shownNode.textContent = String(shown);
    if (bar) bar.style.width = (state.totalItems ? (shown / state.totalItems) * 100 : 100) + '%';
    if (spinner) spinner.hidden = !state.loading;

    if (button) {
      var manual =
        !state.done &&
        (state.mode === 'load_more' ||
          state.errors > 0 ||
          (state.autoPages > 0 && state.autoUsed >= state.autoPages));
      button.hidden = !manual;
      button.href = buildUrl(state.page + 1, false, state.sectionId);
    }

    el.setAttribute('data-state', state.done ? 'done' : state.loading ? 'loading' : 'idle');
  }

  /* ------------------------------------------------------- address bar sync */

  function observeAnchor(node) {
    if (!node) return;
    if (!anchorObserver && 'IntersectionObserver' in window) {
      anchorObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              syncAddressBar(entry.target.getAttribute('data-page-anchor'));
            }
          });
        },
        // only the top sliver of the viewport counts as "where the user is"
        { rootMargin: '0px 0px -85% 0px', threshold: 0 }
      );
    }
    if (anchorObserver) anchorObserver.observe(node);
  }

  /* --------------------------------------------------------------- loading */

  function load(el, automatic) {
    var state = getState(el);
    if (state.loading || state.done) return;

    var page = state.page + 1;
    if (page > state.totalPages) {
      state.done = true;
      paint(el, state);
      return;
    }

    state.loading = true;
    paint(el, state);

    fetch(buildUrl(page, true, state.sectionId), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var incoming = parsed.querySelector('product-list') || parsed.querySelector('.product-list');
        var list = productList(el);
        if (!incoming || !list) throw new Error('Product list not found');

        var fragment = document.createDocumentFragment();
        var appended = [];
        var first = null;
        var index = 0;

        Array.prototype.forEach.call(incoming.children, function (node) {
          var copy = document.importNode(node, true);
          copy.setAttribute('data-appended', '');
          copy.style.setProperty('--appear-delay', Math.min(index, 8) * 40 + 'ms');
          if (!first) {
            copy.setAttribute('data-page-anchor', String(page));
            first = copy;
          }
          fragment.appendChild(copy);
          appended.push(copy);
          index++;
        });

        list.appendChild(fragment);
        observeAnchor(first);

        // Safety net: once the appear animation has had time to run, drop the
        // attribute that carries it, so a product can never stay invisible
        // because an animation was paused or never started.
        window.setTimeout(function () {
          appended.forEach(function (node) {
            node.removeAttribute('data-appended');
            node.style.removeProperty('--appear-delay');
          });
        }, 1200);

        state.page = page;
        state.loading = false;
        state.errors = 0;
        if (automatic) state.autoUsed++;
        if (page >= state.totalPages) state.done = true;

        paint(el, state);

        // The new products may still leave the bottom of the page in view.
        window.setTimeout(schedule, 300);
      })
      .catch(function () {
        state.loading = false;
        state.errors++;
        // After repeated failures we stop interfering: the button is a real
        // link to the next page, so the shopper is never stuck.
        if (state.errors >= MAX_ERRORS) state.giveUp = true;
        paint(el, state);
      });
  }

  /* -------------------------------------------------------------- triggers */

  var ticking = false;

  function schedule() {
    if (ticking) return;
    ticking = true;
    // A timer rather than requestAnimationFrame: rAF is paused in background
    // tabs, and the pagination must already be set up when the tab is opened.
    window.setTimeout(check, 100);
  }

  var watched = false;

  function watchSection() {
    if (watched || !('MutationObserver' in window)) return;
    var section = document.querySelector('.shopify-section--main-collection, .shopify-section--main-search');
    if (!section) return;
    watched = true;
    // The theme swaps the whole section when filters or sorting change.
    new MutationObserver(function () {
      window.setTimeout(boot, 0);
    }).observe(section, { childList: true });
  }

  function boot() {
    watchSection();
    var el = container();
    if (el) getState(el);
    schedule();
  }

  function check() {
    ticking = false;

    var el = container();
    if (!el) return;

    var state = getState(el);
    if (state.loading || state.done) return;
    if (state.mode !== 'infinite') return;
    if (state.errors > 0) return; // after a failure, wait for a real click
    if (state.autoPages > 0 && state.autoUsed >= state.autoPages) return;

    var rect = el.getBoundingClientRect();
    if (rect.top - window.innerHeight < BOTTOM_THRESHOLD) load(el, true);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-infinite-load-more]');
    if (!button) return;

    var el = button.closest(CONTAINER_SELECTOR);
    if (!el) return;

    var state = getState(el);
    if (state.giveUp) return; // let the browser follow the link instead

    event.preventDefault();
    state.autoUsed = 0; // a click resumes automatic loading for another batch
    load(el, false);
  });

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('visibilitychange', boot);
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(boot, 100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();